// SCR-021 `/accounts/$accountId` and SCR-022 `/accounts/$accountId/location/$locationId` (legacy AccountsDetail.vue,
// AccountUsers.vue, AccountLocations.vue, AccountLocationMenu.vue, locations/LocationDetail.vue, LocationUsers.vue;
// field notes backoffice.accounts.detail and backoffice.accounts.location.detail): Radish staff and resellers see one
// account's account-wide users and its locations, and one location's users and pending invitations. The pages act in
// the account (the route switches the session into it, with no location) so the location-scoped identity reads answer.
//
// Create Location, the location menu's Edit and Location Detail's Edit open one dialog (DLG-129, LocationDialog.tsx).
// Invite New User opens the Account User Invite (DLG-134, or No Location, DLG-135); an account user's ⊖ removes them from
// the account (Remove Account User); its pencil, and the location page's Edit User, open Edit User (DLG-136). Not built
// yet (parity checklist): the master-location items (DLG-130–132) and the Live Updates Channel column (OTA, DLG-137).
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import type { AccountChoice } from '@radish/contracts';
import { Button, ChipIndicator, DataTable, TableActionButton, LoadingState, SearchField, legacyFieldsSearchMatches, SECTION_HEADING, useToast, TableActionText, EditIcon, RemoveAccessIcon } from '@radish/ui-web';
import { unwrap, useApi, useLocationQuery, type CallFailure } from '../../data/api.tsx';
import { uuidv7 } from '../../lib/ids.ts';
import { switchTo } from '../../session/session.ts';
import { useSignedIn } from '../../session/use-session.ts';
import { StaffShell } from '../../shell/StaffShell.tsx';
import { MATERIAL_ADMIN_PANEL_SETTINGS, MATERIAL_MANAGE_ACCOUNTS, MaterialIcon } from '../../shell/material-icons.tsx';
import { missingState } from '../../shell/missing-state.ts';
import { LoadError, UI_LINK, UiLinkIcon } from '../../shell/page.tsx';
import { phoneText } from '../branding/LocationInfoPage.tsx';
import { relativeTime, useInvites, useMembers, useRemoveMember, useResendInvite, useCancelInvite, type Invite, type Member } from '../users/data.ts';
import { ChangeRoleDialog, ConfirmDialog, InviteDialog, InviteLinkDialog, MenuItem, RowMenu } from '../users/UsersPage.tsx';
import { AccountInviteDialog, type InviteTarget } from './AccountInviteDialog.tsx';
import { EditUserDialog } from './EditUserDialog.tsx';
import { LocationDialog } from './LocationDialog.tsx';
import { LocationRowMenu } from './LocationRowMenu.tsx';
import { ClearLocationDataDialog } from './ClearLocationDataDialog.tsx';
import { UserAccessDialog } from './UserAccessDialog.tsx';

const ACCOUNT_MEMBERS = 'identity.accountMembers.list';
const ACCOUNT_LOCATIONS = 'tenancy.locations.list';
const ACCOUNT_LOCATIONS_WITH_ITEMS = 'tenancy.locations.withItems';
const LOCATIONS_NO_DATA = { light: '/assets/missing-states/light/missing_state_locations.svg', dark: '/assets/missing-states/dark/missing_state_locations_dark.svg' } as const;

/** The account as the staff session lists it (every account, `via` radish_staff or reseller), or null. */
function useStaffAccount(accountId: string): AccountChoice | null {
  const signed = useSignedIn();
  return signed?.session.accounts.find((a) => a.accountId === accountId && a.via !== 'membership') ?? null;
}

/**
 * Legacy's `color="purple"` and `color="orange"` (B1337): $purple #ad48e0 and $orange #f79206 in light, $purpleDark
 * #c59afe and $orangeDark #ffd497 in dark. Under the AA default the light fills darken in the same hue
 * (`backofficeAaLight`: white on #f79206 is 2.3:1).
 */
const ORANGE = 'bg-orange-button';
const PURPLE = 'bg-purple-button';

const phone = (value: string | null | undefined): string => (value === null || value === undefined || value === '' ? '' : phoneText(value.replace(/\D/g, '')));

/**
 * Legacy PageHeaderAlternate: `first` and `second` apart on one row, 16 px above what follows (q-mb-md). Below 600 px
 * (Quasar's xs) the row stacks bottom-up with 10 px gaps, so `second` (the search, or the header's buttons, last one on
 * top) sits over `first` (B1335).
 */
function HeaderGroup({ first, second }: { readonly first: ReactNode; readonly second: ReactNode }) {
  return (
    <div data-testid="header-group" className="mb-4 flex flex-col-reverse gap-y-[10px] min-[600px]:flex-row min-[600px]:justify-between">
      <div className="flex flex-col-reverse gap-y-[10px] min-[600px]:block">{first}</div>
      <div className="flex flex-col-reverse gap-y-[10px] min-[600px]:flex-row min-[600px]:justify-end min-[600px]:gap-x-[10px]">{second}</div>
    </div>
  );
}

/**
 * Legacy UiSectionHeading with `additional-classes=""`: the title 12 px down (.rad-section-heading) on a 2rem line, and the
 * empty subheading's 4 px (q-mt-xs) under it, so 48 px in all; 'lg' is text-heading-lg (20.8 px), 'sm' text-heading-sm
 * (17.6 px), both bold (B1335).
 */
function SectionTitle({ size, children }: { readonly size: 'lg' | 'sm'; readonly children: ReactNode }) {
  const Tag = size === 'lg' ? 'h2' : 'h3';
  return (
    <div className="pb-1">
      <Tag className={`mt-3 font-bold text-text-1 ${size === 'lg' ? 'text-[1.3rem] leading-8' : 'text-heading-sm'}`}>{children}</Tag>
    </div>
  );
}

/**
 * A section with its heading, count and search (legacy AccountUsers.vue, AccountLocations.vue, LocationUsers.vue): 24 px
 * under the separator (q-mt-lg), a PageHeaderAlternate whose first group is 16 px down (q-mt-md) and holds the heading
 * 8 px left of a 24 px q-avatar (12 px numbers), and the table 16 px under it; from 600 px the table starts 80 px under
 * the search's top (B1335). Every search is `ui-header-input`'s 180 px (Account Users' 177 was legacy's B1020, fixed: L934).
 * The numbers are `count-ink`: white in light and very-dark #1c232e on the pale dark fills (B1337). Legacy's Location
 * Users keeps white on #ffd497 in dark (1.4:1, B1340), fixed by giving it the same ink (L987).
 */
function Section({ title, count, countTestId, tone, term, onTerm, children }: {
  readonly title: string; readonly count: number; readonly countTestId: string; readonly tone: 'purple' | 'orange';
  readonly term: string; readonly onTerm: (v: string) => void; readonly children: ReactNode;
}) {
  return (
    <section className="mt-6">
      <HeaderGroup
        first={
          <div className="mt-4 flex items-center">
            <div className="mr-2"><SectionTitle size="sm">{title}</SectionTitle></div>
            <span data-testid={countTestId} className={`inline-flex size-6 items-center justify-center rounded-full text-[12px] font-normal text-count-ink ${tone === 'purple' ? PURPLE : ORANGE}`}>
              <span>{count}</span>
            </span>
          </div>
        }
        second={<SearchField value={term} onValueChange={onTerm} wide />}
      />
      {children}
    </section>
  );
}

function BackLink({ to, label }: { readonly to: string; readonly label: string }) {
  return (
    // Legacy's UiLink with `caps` (AccountsDetail.vue, LocationDetail.vue).
    <Link to={to as '/accounts'} className={`mb-4 uppercase ${UI_LINK}`}>
      <UiLinkIcon />{label}
    </Link>
  );
}

/** Legacy's page header: a PageHeaderAlternate with a text-heading-lg UiSectionHeading and the action buttons, then a q-separator (B1335). */
function Header({ title, actions }: { readonly title: string; readonly actions?: ReactNode }) {
  return (
    <>
      <HeaderGroup first={<SectionTitle size="lg">{title}</SectionTitle>} second={actions} />
      <hr className="border-line" data-testid="page-header-separator" />
    </>
  );
}

/** Opens a location's dashboard: the session switches to it (legacy `useLocationLogin` / "View Dashboard"). */
function useLogIn(accountId: string) {
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const logIn = async (locationId: string, slug: string) => {
    setBusy(locationId);
    const result = await switchTo(accountId, locationId);
    setBusy(null);
    if (!result.ok) return toast.error(result.message);
    await navigate({ to: `/l/${slug}` });
  };
  return { busy, logIn };
}

function LocationUsersCount({ locationId }: { readonly locationId: string }) {
  const members = useMembers(locationId);
  return <span data-testid="location-users-count">{members.data === undefined ? '' : members.data.members.filter((m) => m.scope === 'location').length}</span>;
}

type LocationRow = { readonly locationId: string; readonly name: string; readonly slug: string; readonly email: string | null; readonly phone: string | null };

export function AccountDetailPage({ accountId }: { readonly accountId: string }) {
  const account = useStaffAccount(accountId);
  const members = useLocationQuery(accountId, ACCOUNT_MEMBERS, {}, (c) => c.queries.identity.accountMembers.list({}));
  const listed = useLocationQuery(accountId, ACCOUNT_LOCATIONS, {}, (c) => c.queries.tenancy.locations.list({}));
  const withItems = useLocationQuery(accountId, ACCOUNT_LOCATIONS_WITH_ITEMS, {}, (c) => c.queries.tenancy.locations.withItems({}));
  // A wipe still running: the row says so (and offers no second one) until every delete is done.
  const clearingIds = useMemo(() => new Set<string>(withItems.data?.clearing ?? []), [withItems.data]);
  const { refetch: refetchWithItems } = withItems;
  useEffect(() => {
    if (clearingIds.size === 0) return undefined;
    const timer = setInterval(() => { void refetchWithItems(); }, 3000);
    return () => clearInterval(timer);
  }, [clearingIds, refetchWithItems]);
  /** Remove Location Data's confirm (DLG-132): the location it would clear, or closed. */
  const [clearing, setClearing] = useState<{ readonly locationId: string; readonly name: string } | null>(null);
  const hasItems = useMemo(() => new Set<string>(withItems.data?.locationIds ?? []), [withItems.data]);
  const { busy, logIn } = useLogIn(accountId);
  const [userTerm, setUserTerm] = useState('');
  const [locationTerm, setLocationTerm] = useState('');
  const [adding, setAdding] = useState<string | null>(null);
  const [link, setLink] = useState<{ readonly email: string; readonly url: string } | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  /** DLG-129: 'new', a location id to edit, or closed. */
  const [locationDialog, setLocationDialog] = useState<string | null>(null);
  const [inviting, setInviting] = useState<InviteTarget | null>(null);
  const [removingUser, setRemovingUser] = useState<Member | null>(null);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const toast = useToast();
  const { client } = useApi();
  const removeAccountUser = useMutation<unknown, CallFailure, string>({
    mutationFn: (userId) => {
      const commandId = uuidv7();
      return unwrap(() => client.commands.identity.removeAccountUser({ accountId: accountId as never, userId: userId as never }, { commandId }));
    },
    onSuccess: () => members.refetch(),
  });

  const users = useMemo(() => (members.data?.members ?? []).filter((m) => legacyFieldsSearchMatches(userTerm, [m.name, m.email, m.phone])), [members.data, userTerm]);
  const locations = useMemo<LocationRow[]>(() => {
    const open = new Set((account?.locations ?? []).map((l) => l.locationId));
    return (listed.data?.locations ?? [])
      .filter((l) => open.has(l.locationId))
      .map((l) => ({ locationId: l.locationId, name: l.name, slug: l.slug, email: l.email ?? null, phone: l.phone ?? null }))
      .filter((l) => legacyFieldsSearchMatches(locationTerm, [l.name, l.email, l.phone]));
  }, [listed.data, account, locationTerm]);

  const userColumns = useMemo<ColumnDef<Member, never>[]>(() => [
    { id: 'name', header: 'User Name', accessorFn: (m) => m.name.toLowerCase() as never, sortingFn: 'text', cell: (c) => c.row.original.name },
    // Legacy AccountUsers.vue sorts by name, email and phone (`sortable: true`), each header keeping its arrow's box (B2114).
    { id: 'email', header: 'Email Address', accessorFn: (m) => m.email.toLowerCase() as never, sortingFn: 'text', cell: (c) => c.row.original.email },
    { id: 'phone', header: 'Phone Number', accessorFn: (m) => (m.phone ?? '') as never, sortingFn: 'text', cell: (c) => phone(c.row.original.phone) },
    {
      // Legacy AccountUsers.vue's own `width: 100px` (B2083); User Access and the two icons draw it wider.
      id: 'actions', header: () => <span className="sr-only">Actions</span>, enableSorting: false, meta: { align: 'right', width: '100px' },
      cell: (c) => (
        <span className="inline-flex items-center">
          <TableActionText aria-label={`User Access for ${c.row.original.name}`} onClick={() => setViewing(c.row.original.userId)}>
            User Access
          </TableActionText>
          <TableActionButton label={`Edit ${c.row.original.name}`} onClick={() => setEditingUser(c.row.original.userId)}><EditIcon /></TableActionButton>
          <TableActionButton label={`Remove ${c.row.original.name} from the account`} onClick={() => setRemovingUser(c.row.original)}><RemoveAccessIcon /></TableActionButton>
        </span>
      ),
    },
  ], []);

  const locationColumns = useMemo<ColumnDef<LocationRow, never>[]>(() => [
    {
      id: 'name', header: 'Location Name', accessorFn: (l) => l.name.toLowerCase() as never, sortingFn: 'text',
      cell: (c) => (
        <span className="inline-flex items-center gap-2">
          <Link to="/accounts/$accountId/location/$locationId" params={{ accountId, locationId: c.row.original.locationId }} className="text-text-1 hover:text-primary dark:text-text-2">{c.row.original.name}</Link>
          {clearingIds.has(c.row.original.locationId) ? <span className="text-[12px] text-text-3" data-testid="location-clearing">Clearing location data…</span> : null}
        </span>
      ),
    },
    // Legacy AccountLocations.vue sorts by name, email and phone too (`sortable: true`; B2114).
    { id: 'email', header: 'Email Address', accessorFn: (l) => (l.email ?? '').toLowerCase() as never, sortingFn: 'text', cell: (c) => c.row.original.email ?? '' },
    { id: 'phone', header: 'Phone Number', accessorFn: (l) => (l.phone ?? '') as never, sortingFn: 'text', cell: (c) => phone(c.row.original.phone) },
    // Legacy AccountLocations centres users_count, header and cell (B2123).
    { id: 'users', header: 'Users', enableSorting: false, meta: { align: 'center' }, cell: (c) => <LocationUsersCount locationId={c.row.original.locationId} /> },
    {
      // Legacy AccountLocations.vue's own `width: 180px`, drawn 180 at 1600 (B2083).
      id: 'actions', header: () => <span className="sr-only">Actions</span>, enableSorting: false, meta: { align: 'right', width: '180px' },
      cell: (c) => {
        const l = c.row.original;
        return (
          // Legacy: a flat, underlined primary "Log in" (q-btn flat no-caps text-underline, 36 tall), not an outlined button,
          // right against the ⋮ with no gap (B2113), at app.scss's raw q-btn weight 400 (B2125).
          <span className="inline-flex items-center">
            <Button variant="flat" className="underline !h-9 !min-w-0 !font-normal" loading={busy === l.locationId} onClick={() => void logIn(l.locationId, l.slug)} aria-label={`Log in to ${l.name}`}>Log in</Button>
            <LocationRowMenu
              name={l.name} onEdit={() => setLocationDialog(l.locationId)} onLogIn={() => void logIn(l.locationId, l.slug)} onAddUser={() => setAdding(l.locationId)}
              onRemoveData={hasItems.has(l.locationId) && !clearingIds.has(l.locationId) ? () => setClearing({ locationId: l.locationId, name: l.name }) : undefined}
            />
          </span>
        );
      },
    },
  ], [accountId, busy, logIn, hasItems, clearingIds]);

  if (account === null) return <StaffShell title="Account Detail"><LoadingState /></StaffShell>;
  return (
    <StaffShell title="Account Detail">
      <div data-screen="backoffice.accounts.detail" data-testid="account-detail-page">
        <BackLink to="/accounts" label="All Accounts" />
        <Header
          title={account.name}
          actions={
            <>
              <Button className="border-transparent! bg-purple-button! text-purple-button-ink! hover:opacity-90" onClick={() => setInviting({ accountId, name: account.name, hasLocation: account.locations.length > 0 })}>Invite New User</Button>
              <Button onClick={() => setLocationDialog('new')}>Create Location</Button>
            </>
          }
        />
        <Section title="Account Users" count={members.data?.members.length ?? 0} countTestId="account-users-count" tone="purple" term={userTerm} onTerm={setUserTerm}>
          {members.isPending ? <LoadingState /> : members.isLoadingError ? <LoadError error={members.error} /> : (
            <DataTable caption="Account Users" testId="account-users-table" columns={userColumns} data={users} getRowId={(m) => m.userId} initialSort={[{ id: 'name', desc: false }]} noDataIllustration={missingState('customers')} noDataTerm={userTerm} />
          )}
        </Section>
        <Section title="Locations" count={account.locations.length} countTestId="account-locations-count" tone="orange" term={locationTerm} onTerm={setLocationTerm}>
          {listed.isPending ? <LoadingState /> : listed.isLoadingError ? <LoadError error={listed.error} /> : (
            // Legacy AccountLocations.vue's BaseTable: MISSING_STATE_LOCATIONS, and a search with no match says so (B1325).
            <DataTable caption="Locations" testId="account-locations-table" columns={locationColumns} data={locations} getRowId={(l) => l.locationId} initialSort={[{ id: 'name', desc: false }]}
              noDataIllustration={LOCATIONS_NO_DATA} noDataTerm={locationTerm} />
          )}
        </Section>
        {adding === null ? null : <InviteDialog locationId={adding} open onClose={() => setAdding(null)} onLink={(email, url) => setLink({ email, url })} sendLabel="Send Invite" roleHint={false} />}
        <InviteLinkDialog link={link} onClose={() => setLink(null)} />
        <UserAccessDialog accountId={accountId} userId={viewing} onClose={() => setViewing(null)} onLogIn={(id, slug) => void logIn(id, slug)} />
        <LocationDialog open={locationDialog !== null} accountId={accountId} locationId={locationDialog === 'new' ? null : locationDialog} onClose={() => setLocationDialog(null)} />
        <ClearLocationDataDialog
          accountId={accountId} location={clearing} onClose={() => setClearing(null)}
          onCleared={() => { toast.success('Location data successfully removed'); void listed.refetch(); void withItems.refetch(); }}
        />
        <AccountInviteDialog target={inviting} onClose={() => setInviting(null)} />
        <EditUserDialog accountId={accountId} userId={editingUser} onClose={() => setEditingUser(null)} />
        <ConfirmDialog
          open={removingUser !== null}
          title="Remove Account User"
          message={`Are you sure you want to delete ${removingUser?.name ?? ''}? This action cannot be undone.`}
          action="Delete"
          busy={removeAccountUser.isPending}
          onClose={() => setRemovingUser(null)}
          onConfirm={() => {
            if (removingUser !== null) {
              removeAccountUser.mutate(removingUser.userId, {
                onSuccess: () => { setRemovingUser(null); toast.success('User removed successfully from account'); },
                onError: (e) => toast.error(e.message),
              });
            }
          }}
        />
      </div>
    </StaffShell>
  );
}

export function StaffLocationPage({ accountId, locationId }: { readonly accountId: string; readonly locationId: string }) {
  const account = useStaffAccount(accountId);
  const toast = useToast();
  const members = useMembers(locationId);
  const invites = useInvites(locationId);
  const remove = useRemoveMember(locationId);
  const resend = useResendInvite(locationId);
  const cancel = useCancelInvite(locationId);
  const { busy, logIn } = useLogIn(accountId);
  const [term, setTerm] = useState('');
  const [inviting, setInviting] = useState(false);
  const [changing, setChanging] = useState<Member | null>(null);
  const [removing, setRemoving] = useState<Member | null>(null);
  const [cancelling, setCancelling] = useState<Invite | null>(null);
  const [link, setLink] = useState<{ readonly email: string; readonly url: string } | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  const [editingLocation, setEditingLocation] = useState(false);
  const [editingMember, setEditingMember] = useState<string | null>(null);
  const now = Date.now();
  const location = account?.locations.find((l) => l.locationId === locationId) ?? null;

  // The location's own users (legacy `location_user`); account-wide users are on the account.
  const own = useMemo(() => (members.data?.members ?? []).filter((m) => m.scope === 'location'), [members.data]);
  const rows = useMemo(() => own.filter((m) => legacyFieldsSearchMatches(term, [m.name, m.email, m.phone])), [own, term]);

  const columns = useMemo<ColumnDef<Member, never>[]>(() => [
    { id: 'name', header: 'User Name', accessorFn: (m) => m.name.toLowerCase() as never, sortingFn: 'text', cell: (c) => c.row.original.name },
    // Legacy LocationUsers.vue sorts Email Address and Phone Number too (`sortable: true`), lower-cased as Quasar's sort (B2084).
    { id: 'email', header: 'Email Address', accessorFn: (m) => m.email.toLowerCase() as never, sortingFn: 'text', cell: (c) => c.row.original.email },
    { id: 'phone', header: 'Phone Number', accessorFn: (m) => (m.phone ?? '') as never, sortingFn: 'text', cell: (c) => phone(c.row.original.phone) },
    // Legacy LocationUsers.vue: a filled primary q-badge with the role as stored (12 px on 12, 2 by 6 px, radius 4); dark
    // keeps User Access' inverted ink, where legacy left it white on #b4d5ac (B2084, as B1347).
    { id: 'role', header: 'Role', enableSorting: false, cell: (c) => <span className="inline-flex rounded bg-primary px-1.5 py-0.5 text-[12px] leading-3 font-normal text-white dark:text-text-inverted">{c.row.original.roleName}</span> },
    {
      // Legacy TableActions: four inline round buttons with their tooltips, in the column's own `width: 100px` (B2084).
      id: 'actions', header: () => <span className="sr-only">Actions</span>, enableSorting: false, meta: { align: 'right', width: '100px' },
      cell: (c) => (
        <span className="inline-flex items-center">
          <TableActionButton label="User Role" onClick={() => setChanging(c.row.original)}><MaterialIcon path={MATERIAL_ADMIN_PANEL_SETTINGS} size={20} className="!size-5 text-primary" /></TableActionButton>
          <TableActionButton label="User Access" onClick={() => setViewing(c.row.original.userId)}><MaterialIcon path={MATERIAL_MANAGE_ACCOUNTS} size={20} className="!size-5" /></TableActionButton>
          <TableActionButton label={`Edit ${c.row.original.name}`} onClick={() => setEditingMember(c.row.original.userId)}><EditIcon /></TableActionButton>
          <TableActionButton label={`Remove User ${c.row.original.name}`} onClick={() => setRemoving(c.row.original)}><RemoveAccessIcon /></TableActionButton>
        </span>
      ),
    },
  ], []);

  const inviteColumns = useMemo<ColumnDef<Invite, never>[]>(() => [
    { id: 'email', header: 'Email', accessorFn: (i) => i.email as never, cell: (c) => c.row.original.email },
    { id: 'invitedBy', header: 'Invited By', enableSorting: false, cell: (c) => c.row.original.invitedBy ?? '-' },
    { id: 'sent', header: 'Sent', accessorFn: (i) => i.sentAt as never, cell: (c) => relativeTime(c.row.original.sentAt, now) },
    { id: 'expires', header: 'Expires', accessorFn: (i) => i.expiresAt as never, cell: (c) => (c.row.original.expired ? <ChipIndicator tone="negative">Expired</ChipIndicator> : relativeTime(c.row.original.expiresAt, now)) },
    {
      id: 'actions', header: () => <span className="sr-only">Actions</span>, enableSorting: false, meta: { align: 'right', width: '80px' },
      cell: (c) => (
        <RowMenu label={`Actions for the invitation to ${c.row.original.email}`}>
          <MenuItem disabled={resend.isPending} onSelect={() => resend.mutate({ inviteId: c.row.original.inviteId, delivery: 'email' }, { onSuccess: () => toast.success('Invitation resent'), onError: (e) => toast.error(e.message) })}>Resend</MenuItem>
          <MenuItem negative onSelect={() => setCancelling(c.row.original)}>Cancel</MenuItem>
        </RowMenu>
      ),
    },
  ], [now, resend, toast]);

  if (account === null || location === null) return <StaffShell title="Accounts"><LoadingState /></StaffShell>;
  const pending = invites.data?.invites ?? [];
  return (
    <StaffShell title="Accounts">
      <div data-screen="backoffice.accounts.location.detail" data-testid="staff-location-page">
        <BackLink to={`/accounts/${accountId}`} label={account.name} />
        <Header
          title={location.name}
          actions={
            <>
              <Button className="border-transparent! bg-orange-button! text-orange-button-ink! hover:opacity-90" onClick={() => setInviting(true)}>Invite New User</Button>
              <Button variant="outline" onClick={() => setEditingLocation(true)}>Edit</Button>
              <Button loading={busy === locationId} onClick={() => void logIn(locationId, location.slug)}>View Dashboard</Button>
            </>
          }
        />
        <Section title="Location Users" count={own.length} countTestId="location-users-count" tone="orange" term={term} onTerm={setTerm}>
          {members.isPending ? <LoadingState /> : members.isLoadingError ? <LoadError error={members.error} /> : (
            <DataTable
              caption="Location Users"
              testId="location-users-table"
              columns={columns}
              data={rows}
              getRowId={(m) => m.userId}
              initialSort={[{ id: 'name', desc: false }]}
              // Legacy LocationUsers.vue's BaseTable no-data block: the customers icon, "No Data", or the term while searching (B1555).
              noDataIllustration={missingState('customers')}
              noDataTerm={term}
            />
          )}
        </Section>
        {pending.length > 0 ? (
          // Legacy LocationUsers.vue's UiSectionHeading (q-mt-lg q-mb-sm): 27.5 under the "Displaying" line, 8 over the table (B1339).
          <section className="mt-6" data-testid="pending-invitations">
            <h3 className={`mb-2 ${SECTION_HEADING}`}>Pending Invitations</h3>
            <DataTable caption="Pending Invitations" columns={inviteColumns} data={pending} getRowId={(i) => i.inviteId} initialSort={[{ id: 'sent', desc: true }]} />
          </section>
        ) : null}
        <InviteDialog locationId={locationId} open={inviting} onClose={() => setInviting(false)} onLink={(email, url) => setLink({ email, url })} sendLabel="Send Invite" roleHint={false} />
        <InviteLinkDialog link={link} onClose={() => setLink(null)} />
        <ChangeRoleDialog locationId={locationId} member={changing} onClose={() => setChanging(null)} />
        <LocationDialog open={editingLocation} accountId={accountId} locationId={locationId} onClose={() => setEditingLocation(false)} />
        <EditUserDialog accountId={accountId} userId={editingMember} onClose={() => setEditingMember(null)} />
        <UserAccessDialog accountId={accountId} userId={viewing} onClose={() => setViewing(null)} onLogIn={(id, slug) => void logIn(id, slug)} />
        <ConfirmDialog
          open={removing !== null}
          title="Remove User"
          message={`Are you sure you want to remove ${removing?.name ?? ''} from this location?`}
          action="Remove"
          busy={remove.isPending}
          onClose={() => setRemoving(null)}
          onConfirm={() => {
            if (removing !== null) remove.mutate(removing.userId, { onSuccess: () => { setRemoving(null); toast.success('User removed'); }, onError: (e) => toast.error(e.message) });
          }}
        />
        <ConfirmDialog
          open={cancelling !== null}
          title="Cancel Invitation"
          message={`Are you sure you want to cancel the invitation to ${cancelling?.email ?? ''}?`}
          action="Cancel Invitation"
          busy={cancel.isPending}
          onClose={() => setCancelling(null)}
          onConfirm={() => {
            if (cancelling !== null) cancel.mutate(cancelling.inviteId, { onSuccess: () => { setCancelling(null); toast.success('Invitation cancelled'); }, onError: (e) => toast.error(e.message) });
          }}
        />
      </div>
    </StaffShell>
  );
}
